/**
 * <Skeleton /> — primitive shimmer placeholder for loading states.
 *
 * Standardises the ad-hoc `animate-pulse + bg-zinc-800 rounded` pattern that
 * was repeated across ~17 files. Variants match the common shapes in MCT's
 * dark theme (cards, tables, charts, text lines).
 *
 * Usage:
 *   <Skeleton variant="text" w="120px" />
 *   <Skeleton variant="card" />               // a full card placeholder
 *   <Skeleton variant="table-row" rows={5} /> // 5-row table skeleton
 *   <Skeleton variant="chart" h="240px" />
 *   <Skeleton variant="rect" w="100%" h="48px" />
 */
import type { CSSProperties } from "react";

type Variant = "text" | "card" | "chart" | "table-row" | "panel" | "circle" | "rect" | "stat-tile";

interface Props {
  variant?:   Variant;
  w?:         string | number;   // width (any CSS value)
  h?:         string | number;   // height (any CSS value)
  className?: string;
  rows?:      number;            // for variant="table-row"
  count?:     number;            // for variant="stat-tile" (number of tiles)
}

const BASE = "bg-zinc-800/70 animate-pulse";

function _toCss(v: string | number | undefined): string | undefined {
  if (v === undefined) return undefined;
  return typeof v === "number" ? `${v}px` : v;
}

export default function Skeleton({
  variant = "rect",
  w,
  h,
  className = "",
  rows = 5,
  count = 4,
}: Props) {
  const style: CSSProperties = {
    width:  _toCss(w),
    height: _toCss(h),
  };

  switch (variant) {
    case "text":
      return (
        <span
          className={`inline-block h-3 rounded ${BASE} ${className}`}
          style={{ width: _toCss(w) ?? "80px", height: _toCss(h) ?? "12px" }}
        />
      );

    case "circle":
      return (
        <div
          className={`rounded-full ${BASE} ${className}`}
          style={{ width: _toCss(w) ?? "32px", height: _toCss(h) ?? "32px" }}
        />
      );

    case "stat-tile":
      // Mimics the dashboard top-row stat cards: label + big number + sub.
      return (
        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-${Math.min(count, 6)} gap-2 sm:gap-3 ${className}`}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="rounded-xl bg-zinc-900/50 border border-zinc-800/60 px-3 sm:px-4 py-3">
              <div className={`h-2.5 w-20 rounded ${BASE} mb-3`} />
              <div className={`h-7 w-28 rounded ${BASE} mb-2`} />
              <div className={`h-2 w-16 rounded ${BASE}`} />
            </div>
          ))}
        </div>
      );

    case "card":
      return (
        <div
          className={`rounded-xl bg-zinc-900/50 border border-zinc-800/60 p-3 sm:p-5 ${className}`}
          style={style}
        >
          <div className={`h-3 w-32 rounded ${BASE} mb-4`} />
          <div className={`h-2.5 w-full rounded ${BASE} mb-2`} />
          <div className={`h-2.5 w-5/6 rounded ${BASE} mb-2`} />
          <div className={`h-2.5 w-4/6 rounded ${BASE}`} />
        </div>
      );

    case "panel":
      return (
        <div
          className={`rounded-xl bg-[#111118] border border-zinc-800/60 p-3 sm:p-5 ${className}`}
          style={{ minHeight: _toCss(h) ?? "120px", ...style }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className={`h-3 w-40 rounded ${BASE}`} />
            <div className={`h-3 w-16 rounded ${BASE}`} />
          </div>
          <div className="space-y-2">
            <div className={`h-2.5 w-full rounded ${BASE}`} />
            <div className={`h-2.5 w-11/12 rounded ${BASE}`} />
            <div className={`h-2.5 w-10/12 rounded ${BASE}`} />
          </div>
        </div>
      );

    case "chart":
      return (
        <div
          className={`rounded-xl bg-[#111118] border border-zinc-800/60 p-3 sm:p-4 ${className}`}
          style={style}
        >
          <div className="flex items-center justify-between mb-3">
            <div className={`h-3 w-36 rounded ${BASE}`} />
            <div className={`h-3 w-20 rounded ${BASE}`} />
          </div>
          <div
            className={`w-full rounded ${BASE}`}
            style={{ height: _toCss(h) ?? "200px" }}
          />
        </div>
      );

    case "table-row":
      return (
        <div className={`space-y-1.5 ${className}`}>
          {/* header */}
          <div className="flex items-center gap-3 px-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`h-2 rounded ${BASE}`} style={{ width: i === 0 ? "20%" : i === 4 ? "12%" : "16%" }} />
            ))}
          </div>
          {/* rows */}
          {Array.from({ length: rows }).map((_, r) => (
            <div key={r} className="flex items-center gap-3 px-2 py-2 rounded bg-zinc-900/30">
              {Array.from({ length: 5 }).map((_, c) => (
                <div key={c} className={`h-3 rounded ${BASE}`} style={{ width: c === 0 ? "20%" : c === 4 ? "12%" : "16%" }} />
              ))}
            </div>
          ))}
        </div>
      );

    case "rect":
    default:
      return (
        <div
          className={`rounded ${BASE} ${className}`}
          style={{ width: _toCss(w) ?? "100%", height: _toCss(h) ?? "16px" }}
        />
      );
  }
}
